import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useCart } from "@/contexts/CartContext";
import PageTransition from "@/components/PageTransition";
import { Menu, X, Phone, MapPin } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_morges-ramen/artifacts/b8jd0rbw_Logo%20Hanamiya.jpg";

const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/menu", label: "Menu", external: true },
  { to: "/commander", label: "Commander" },
  { to: "/reserver", label: "Réserver" },
];

export default function Layout() {
  const { count } = useCart();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onHome = pathname === "/";
  const transparent = onHome && !scrolled;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          transparent ? "bg-transparent" : "bg-background/85 backdrop-blur-md border-b border-border"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center overflow-hidden transition ${
              transparent ? "bg-white/15 backdrop-blur ring-1 ring-white/30" : "bg-white ring-1 ring-border"
            }`}>
              <img src={LOGO_URL} alt="Hanamiya Ramen" className="w-10 h-10 object-contain" />
            </div>
            <div className="flex flex-col leading-none">
              <span className={`font-display text-2xl ${transparent ? "text-white" : "text-foreground"}`}>Hanamiya</span>
              <span className={`text-[10px] tracking-[0.3em] uppercase ${transparent ? "text-white/80" : "text-muted-foreground"}`}>Ramen · Morges</span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {NAV.map((n) =>
              n.external ? (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  className={`nav-link text-sm tracking-wide ${transparent ? "text-white" : "text-foreground"}`}
                >
                  {n.label}
                </Link>
              ) : (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `nav-link text-sm tracking-wide ${transparent ? "text-white" : "text-foreground"} ${isActive ? "font-semibold" : ""}`
                  }
                >
                  {n.label}
                </NavLink>
              )
            )}
            {count > 0 && (
              <Link
                to="/checkout"
                data-testid="header-cart-link"
                className="text-sm bg-primary text-white px-4 py-2 rounded-sm hover:bg-primary/90 transition"
              >
                Panier · {count}
              </Link>
            )}
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            className={`md:hidden p-2 ${transparent ? "text-white" : "text-foreground"}`}
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {open && (
          <div className="md:hidden bg-background border-t border-border">
            <div className="px-6 py-4 flex flex-col gap-4">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  onClick={() => setOpen(false)}
                  className="text-foreground py-2"
                >
                  {n.label}
                </Link>
              ))}
              {count > 0 && (
                <Link
                  to="/checkout"
                  onClick={() => setOpen(false)}
                  className="bg-primary text-white px-4 py-2 rounded-sm text-center"
                  data-testid="mobile-cart-link"
                >
                  Panier · {count}
                </Link>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>
      <PageTransition />

      <footer data-testid="site-footer" className="bg-primary text-white mt-24 seigaiha">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-3 gap-12">
          <div>
            <h3 className="font-display text-3xl mb-4">Hanamiya Ramen</h3>
            <p className="text-white/70 text-sm leading-relaxed">
              L'art du ramen artisanal à Morges. Bouillons mijotés 14 heures, nouilles fraîches, ingrédients sélectionnés.
            </p>
          </div>
          <div>
            <h4 className="uppercase tracking-[0.2em] text-xs mb-4 text-white/60">Visitez-nous</h4>
            <div className="flex items-start gap-3 mb-3"><MapPin size={18} className="mt-1 shrink-0" /><div className="text-sm">Rue de la Gare 8<br />1110 Morges</div></div>
            <div className="flex items-start gap-3"><Phone size={18} className="mt-1 shrink-0" /><a href="tel:0218020593" className="text-sm hover:underline">021 802 05 93</a></div>
          </div>
          <div>
            <h4 className="uppercase tracking-[0.2em] text-xs mb-4 text-white/60">Horaires</h4>
            <div className="text-sm space-y-1 text-white/80">
              <p>Lundi · Mercredi → Dimanche</p>
              <p className="text-white">11h00 — 14h30</p>
              <p className="text-white">18h00 — 22h30</p>
              <p className="text-white/60 mt-2">Mardi · fermé</p>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10 py-6 text-center text-xs text-white/50">
          © {new Date().getFullYear()} Hanamiya Ramen — Tous droits réservés
        </div>
      </footer>
    </div>
  );
}
